import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TranslatableLabel } from '../../../hooks/useResolvedLabel';

const RESET_LABEL_TIMER = 3000;

type UseSocialIconWithSuccessStateArgs = {
	label: TranslatableLabel;
	successLabel?: TranslatableLabel;
};

type UseSocialIconWithSuccessStateResult = {
	data: {
		currentLabel: TranslatableLabel;
		successState: boolean | undefined;
	};
	actions: {
		afterShare: (success: boolean) => void;
	};
};

export function useSocialIconWithSuccessState({
	label,
	successLabel,
}: UseSocialIconWithSuccessStateArgs): UseSocialIconWithSuccessStateResult {
	const [successState, setSuccessState] = useState<boolean | undefined>(undefined);

	const afterShare = useCallback((success: boolean) => {
		setSuccessState(success);
	}, []);

	useEffect(() => {
		if (typeof successState === 'undefined') return undefined;
		const timeout = window.setTimeout(() => setSuccessState(undefined), RESET_LABEL_TIMER);
		return () => window.clearTimeout(timeout);
	}, [successState]);

	const currentLabel = useMemo(() => {
		if (successState === true && successLabel) return successLabel;
		return label;
	}, [successState, successLabel, label]);

	return {
		data: { currentLabel, successState },
		actions: { afterShare },
	};
}
